import { useState, type ReactNode } from "react"
import { AppWindow, Archive, ClipboardList, Mail, Zap, type LucideIcon } from "lucide-react"
import { useSupportProjects } from "@/lib/api/support"
import { VIA_LABEL, VIA_REPLY, brandColor, logoUrl, type SupportVia } from "@/lib/support"
import { cn } from "@/lib/utils"

/**
 * A product's icon: its own logo (project settings.logo_url) on a rounded
 * square with a hairline in its brand colour, or the brand colour with the
 * name's initial when there is no logo or it fails to load. Pass the slug and
 * the rest comes from the projects list (cached), or pass it all directly.
 */
export function ProductMark({
  slug,
  name,
  color,
  logo,
  size = 16,
  className,
}: {
  slug?: string | null
  name?: string | null
  color?: string | null
  logo?: string | null
  size?: number
  className?: string
}) {
  const { data: projects } = useSupportProjects()
  const project = slug ? projects?.find((p) => p.slug === slug) : undefined
  const src = logo ?? logoUrl(project?.settings)
  const c = brandColor(color ?? project?.brand_color)
  const label = name ?? project?.name ?? slug ?? ""
  const [failed, setFailed] = useState<string | null>(null)
  const radius = Math.max(3, Math.round(size * 0.24))
  const box = { width: size, height: size, borderRadius: radius, boxShadow: `0 0 0 1px ${c}55` }

  if (src && failed !== src) {
    return (
      <img
        src={src}
        alt=""
        title={label || undefined}
        width={size}
        height={size}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(src)}
        className={cn("shrink-0 bg-white object-cover", className)}
        style={box}
      />
    )
  }
  return (
    <span
      aria-hidden
      title={label || undefined}
      className={cn("inline-flex shrink-0 items-center justify-center font-semibold leading-none text-white", className)}
      style={{ ...box, backgroundColor: c, fontSize: Math.max(8, Math.round(size * 0.58)) }}
    >
      {(label.trim()[0] ?? "?").toUpperCase()}
    </span>
  )
}

/** The project's icon and name. */
export function ProjectChip({ slug, name, color, className }: { slug?: string | null; name: string | null; color: string | null; className?: string }) {
  return (
    <span className={cn("inline-flex h-5 max-w-[12rem] shrink-0 items-center gap-1.5 truncate text-xs font-medium text-foreground", className)}>
      <ProductMark slug={slug} name={name} color={color} size={16} />
      <span className="truncate">{name ?? "Unknown project"}</span>
    </span>
  )
}

/** A label over a value in the side panel. */
export function PanelRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <div className="min-w-0 break-words text-sm text-foreground">{children}</div>
    </div>
  )
}

/** A warning band across the top of a thread or form. */
export function Banner({ tone, icon, children }: { tone: "warning" | "violet" | "error"; icon?: ReactNode; children: ReactNode }) {
  return (
    <div
      role="status"
      className={cn(
        "flex items-start gap-2 rounded-2xl border px-3 py-2 text-sm",
        tone === "warning" && "border-[#ffde0080] bg-[#ffde001f] text-[#7a4a00] dark:border-[#fa820040] dark:bg-[#fa820014] dark:text-[#ffca16]",
        tone === "violet" && "border-[#4400ee30] bg-[#4400ee0a] text-[#1f0099] dark:border-[#8354fe50] dark:bg-[#8354fe1a] dark:text-[#baa7ff]",
        tone === "error" && "border-[#f3000d40] bg-[#f3000d0d] text-[#c40006] dark:border-[#ff173f50] dark:bg-[#ff173f1a] dark:text-[#ff9592]",
      )}
    >
      {icon && <span className="mt-0.5 shrink-0 [&_svg]:h-4 [&_svg]:w-4">{icon}</span>}
      <div className="min-w-0">{children}</div>
    </div>
  )
}

const VIA_ICON: Record<SupportVia, LucideIcon> = {
  email: Mail,
  in_app: AppWindow,
  contact: ClipboardList,
  api: Zap,
  import: Archive,
}

/** How a conversation arrived (and so how the customer gets replies): an icon and a label. */
export function ViaTag({ via, detail, iconOnly, className }: { via: SupportVia; detail?: boolean; iconOnly?: boolean; className?: string }) {
  const Icon = VIA_ICON[via]
  if (iconOnly) {
    return (
      <span title={`${VIA_LABEL[via]}: ${VIA_REPLY[via]}`} aria-label={VIA_LABEL[via]} className={cn("inline-flex shrink-0 items-center text-muted-foreground", className)}>
        <Icon aria-hidden className="h-3.5 w-3.5" />
      </span>
    )
  }
  return (
    <span title={VIA_REPLY[via]} className={cn("inline-flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground", className)}>
      <Icon aria-hidden className="h-3 w-3" />
      <span>{VIA_LABEL[via]}</span>
      {detail && <span className="text-muted-foreground/80">· {VIA_REPLY[via]}</span>}
    </span>
  )
}
